import React, { useCallback, useEffect, useRef, useState } from "react";
import { getRepuestosMe } from "../api";
import { errMsg } from "../utils";
import RepuestosInicio from "./RepuestosInicio";
import RepuestosAutorizaciones from "./RepuestosAutorizaciones";
import RepuestosDetalle from "./RepuestosDetalle";
import { RepIcon } from "./RepuestosUI";
import "./Repuestos.css";

const LOGO = process.env.PUBLIC_URL + "/fs-black.png";
const NAV = [
  { key: "inicio", label: "Resumen", icon: "home" },
  { key: "lista", label: "Pendientes", icon: "document" },
  { key: "archivo", label: "Archivo", icon: "archive" },
  { key: "facturar", label: "Subir factura", icon: "upload" },
];
const TITLES = { inicio: "Resumen", lista: "Pendientes", archivo: "Archivo de autorizaciones", facturar: "Subir factura", detalle: "Detalle de autorización" };
const initialQuery = () => ({ estado: "", q: "", desde: "", hasta: "", page: 1, page_size: 25 });

function initials(name) {
  return String(name || "P").trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

export default function RepuestosPortal({ onLogout }) {
  const [me, setMe] = useState(null);
  const [meError, setMeError] = useState("");
  const [meLoading, setMeLoading] = useState(true);
  const [screen, setScreen] = useState("inicio");
  const [listViews, setListViews] = useState({ lista: initialQuery(), archivo: initialQuery(), facturar: initialQuery() });
  const [selId, setSelId] = useState(null);
  const [focusFactura, setFocusFactura] = useState(false);
  const [detailView, setDetailView] = useState("gestion");
  const [backTo, setBackTo] = useState("lista");
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);
  const contentRef = useRef(null);

  const loadMe = useCallback(async (silent) => {
    if (!silent) { setMeLoading(true); setMeError(""); }
    try { setMe(await getRepuestosMe()); setMeError(""); }
    catch (e) {
      if (e?.response?.status !== 401) setMeError(errMsg(e, "No se pudo cargar su cuenta. Intente de nuevo."));
    } finally { if (!silent) setMeLoading(false); }
  }, []);

  useEffect(() => { loadMe(false); }, [loadMe]);
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  useEffect(() => { contentRef.current?.focus({ preventScroll: true }); }, [screen]);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 4500);
  }
  function go(next) { setScreen(next); window.scrollTo(0, 0); }
  function openList(estado) {
    const target = ["APROBADA", "ANULADA"].includes(estado) ? "archivo" : "lista";
    setListViews((prev) => ({ ...prev, [target]: { ...initialQuery(), estado: estado || "" } }));
    go(target);
  }
  function updateList(next) { setListViews((prev) => ({ ...prev, [screen]: next })); }
  function openDetail(id, opts) {
    setSelId(id);
    setFocusFactura(Boolean(opts?.facturar));
    setDetailView(opts?.documento || screen === "archivo" ? "documento" : "gestion");
    setBackTo(["facturar", "archivo"].includes(screen) ? screen : "lista");
    go("detalle");
  }

  const supplierName = me?.supplier?.name || "Proveedor";
  const active = screen === "detalle" ? backTo : screen;
  const pendientes = Number(me?.counts?.nuevas || 0) + Number(me?.counts?.por_facturar || 0) + Number(me?.counts?.devueltas || 0);

  return <div className="prov-shell repuestos-app">
    <a className="rep-skip" href="#contenido">Saltar al contenido</a>
    <aside className="prov-sidebar no-print">
      <button type="button" className="rep-brand" onClick={() => go("inicio")} aria-label="ALAS Proveedores, ir al resumen">
        <span className="rep-brand-symbol"><img src={LOGO} alt=""/></span><span><strong>ALAS<span className="rep-brand-dot">.</span></strong><small>Portal de proveedores</small></span>
      </button>
      <div className="rep-nav-label">ÁREA DE TRABAJO</div>
      <nav className="rep-side-nav" aria-label="Navegación principal">{NAV.map((n) => <button type="button" key={n.key} className={`rep-nav-item ${active === n.key ? "active" : ""}`} aria-current={active === n.key ? "page" : undefined} onClick={() => go(n.key)}><RepIcon name={n.icon}/><span>{n.label}</span>{n.key === "facturar" && pendientes > 0 && <span className="rep-nav-count">{pendientes}</span>}</button>)}</nav>
      <div className="rep-side-note"><RepIcon name="shield"/><div><strong>Su gestión, en un solo lugar</strong><p>Consulte autorizaciones, envíe facturas y siga su revisión.</p></div></div>
      <div className="rep-side-account"><span className="rep-avatar">{initials(supplierName)}</span><div><strong title={supplierName}>{supplierName}</strong><small>Proveedor autorizado</small></div></div>
      <button type="button" className="rep-side-logout" onClick={onLogout}><RepIcon name="logout" size={18}/>Cerrar sesión</button>
    </aside>
    <div className="prov-main">
      <header className="prov-header no-print">
        <div className="rep-header-path"><span className="rep-mobile-mark"><img src={LOGO} alt="ALAS"/></span><span className="rep-header-section">Proveedores</span><span className="rep-path-divider">/</span><span>{TITLES[screen]}</span></div>
        <div className="rep-header-account"><span className="rep-header-avatar"><RepIcon name="building" size={18}/></span><div><strong title={supplierName}>{supplierName}</strong><small>{me?.supplier?.document_number ? `NIT ${me.supplier.document_number}` : "Cuenta de proveedor"}</small></div><button type="button" className="rep-mobile-logout" onClick={onLogout} aria-label="Cerrar sesión"><RepIcon name="logout" size={20}/></button></div>
      </header>
      <main id="contenido" className="prov-content" tabIndex={-1} ref={contentRef}>
        {meError && screen !== "inicio" && <div className="banner err" role="alert">{meError} <button type="button" className="btn-sm ghost" onClick={() => loadMe(false)}>Reintentar</button></div>}
        {screen === "inicio" && <RepuestosInicio me={me} loading={meLoading} meError={meError} onRetry={() => loadMe(false)} onOpenList={openList} onInvoice={() => go("facturar")}/>}
        {["lista", "archivo", "facturar"].includes(screen) && <RepuestosAutorizaciones key={screen} mode={screen} query={listViews[screen]} onQueryChange={updateList} onOpen={openDetail} onRefreshCounts={() => loadMe(true)}/>}
        {screen === "detalle" && selId != null && <RepuestosDetalle key={selId} id={selId} company={me?.company} initialView={detailView} focusFactura={focusFactura} onBack={() => go(backTo)} onToast={showToast} onChanged={() => loadMe(true)}/>}
        <footer className="rep-page-footer"><span>ALAS · Portal de proveedores</span><span>Autorizaciones y facturación de repuestos</span></footer>
      </main>
    </div>
    <nav className="rep-mobile-nav no-print" aria-label="Navegación móvil">{NAV.map((n) => <button type="button" key={n.key} className={active === n.key ? "active" : ""} aria-current={active === n.key ? "page" : undefined} onClick={() => go(n.key)}><RepIcon name={n.icon} size={21}/><span>{n.label}</span></button>)}</nav>
    {toast && <div className="toast no-print" role="status"><RepIcon name="check" size={18}/>{toast}</div>}
  </div>;
}
